import React, { useContext, useCallback, useRef, type FC } from 'react';
import { FormattedMessage } from 'react-intl';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import {
	FlyoutMenuItem,
	FlyoutMenuItemContent,
	FlyoutMenuItemTrigger,
} from '@atlaskit/side-nav-items/flyout-menu-item';

import { useRouteDataRef, useRouteUrl } from '@confluence/route-manager/entry-points/RouteState.ts';
import type { LoadableType } from '@confluence/loadable';
import { LoadableLazy } from '@confluence/loadable';
import { createLazyCallbackHook } from '@confluence/loadable/entry-points/lazy-callback.ts';
import {
	GO_TO_RECENT_SHORTCUT,
	UNIVERSAL_GO_TO_RECENT_SHORTCUT,
} from '@confluence/shortcuts/entry-points/confluence-shortcuts.ts';
import { APP_NAV_PAGES_DROPDOWN_EXPERIENCE } from '@confluence/experience-tracker/entry-points/ExperienceName.ts';
import { ExperienceTrackerContext } from '@confluence/experience-tracker/entry-points/component.ts';
import { ExperienceTimeout } from '@confluence/experience-tracker/entry-points/ExperienceTimeout.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { useIsEditorOpen } from '@confluence/page-utils/entry-points/useIsEditorOpen.ts';
import { HOME } from '@confluence/named-routes/entry-points/routes.ts';
import { useFlyout } from '@confluence/flyout-store';

import { KeyboardShortcutWrapper } from '../../PlatformNavigation/KeyboardShortcutWrapper.tsx';
import { i18n } from '../globalNavigationTranslations.ts';
import { ClockIconComponent } from '../GlobalNavigationIcons.tsx';
import { preloadRecentContentMenu } from '../../ContentMenu/preloadRecentContentMenu.ts';
import { ContentMenuLoadingState } from '../../ContentMenu/ContentMenuLoadingStates.tsx';

import { useGlobalItemVisibility } from './useGlobalItemVisibility.ts';
import type { GlobalItemProps } from './globalItemProps.ts';

export const RecentContentMenuLoader: LoadableType<{}> = LoadableLazy({
	loader: async () =>
		(
			await import(
				/* webpackChunkName: "loadable-RecentContentMenu" */
				'../../ContentMenu/RecentContentMenu.tsx'
			)
		).RecentContentMenu,
	loading: () => <ContentMenuLoadingState />,
});

const useLazyClickAnalytics = createLazyCallbackHook(
	async () =>
		(await import(/* webpackChunkName: "loadable-analyticsCallbacks" */ '../analyticsCallbacks.ts'))
			.fireRecentsClickedAnalytics,
);

const MENU_ID = 'recent';

export const RecentItem: FC<GlobalItemProps> = ({ isHidden, peekingId, setPeekingId }) => {
	const { isFlyoutOpen, openFlyout, closeFlyout } = useFlyout(MENU_ID);

	const routeDataRef = useRouteDataRef();
	const { cloudId } = useSessionData();
	const hasBeenOpenedBefore = useRef(false);
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const fireClickAnalytics = useLazyClickAnalytics(createAnalyticsEvent, routeDataRef);
	const experienceTracker = useContext(ExperienceTrackerContext);
	const isEditorOpen = useIsEditorOpen();

	const abortExperienceTracker = useCallback(() => {
		experienceTracker.abort({
			name: APP_NAV_PAGES_DROPDOWN_EXPERIENCE,
			reason: 'Primary dropdown menu: closed by user',
			attributes: {
				navVersion: '4',
				dropdownType: 'recent',
			},
		});
	}, [experienceTracker]);

	const startExperienceTracker = useCallback(() => {
		experienceTracker.start({
			name: APP_NAV_PAGES_DROPDOWN_EXPERIENCE,
			timeout: ExperienceTimeout.NAVIGATION_LOAD,
			attributes: {
				navVersion: '4',
				dropdownType: 'recent',
			},
		});
	}, [experienceTracker]);

	const onOpenChange = useCallback(
		(newIsOpen: boolean) => {
			if (!newIsOpen) {
				abortExperienceTracker();
				isFlyoutOpen && closeFlyout();
				// onOpenChange is called whenever FlyoutMenuItem component mounts, but we
				// only want to fire analytics event on user interaction. We check here if the
				// flyout has been opened before to ignore onOpenChange calls before user interaction
				if (hasBeenOpenedBefore.current) {
					void fireClickAnalytics(false);
				}
			} else {
				openFlyout();
				hasBeenOpenedBefore.current = true;
			}
		},
		[abortExperienceTracker, isFlyoutOpen, closeFlyout, fireClickAnalytics, openFlyout],
	);

	const onShortcutTriggered = useCallback(() => {
		if (!isFlyoutOpen) {
			void fireClickAnalytics(true, true, isHidden);
			startExperienceTracker();
			openFlyout();
		}
	}, [fireClickAnalytics, isFlyoutOpen, openFlyout, startExperienceTracker, isHidden]);

	const onClick = useCallback(() => {
		if (!isFlyoutOpen) {
			openFlyout();
			startExperienceTracker();
			void fireClickAnalytics(true); //if we clicked on it we know it's not hidden or fired via keyboard shortcut
		} else {
			closeFlyout();
		}
	}, [fireClickAnalytics, isFlyoutOpen, openFlyout, closeFlyout, startExperienceTracker]);

	const onHover = useCallback(() => {
		if (!isFlyoutOpen) {
			void RecentContentMenuLoader.preload();
			void preloadRecentContentMenu(cloudId);
		}
	}, [isFlyoutOpen, cloudId]);

	const isSelected = useRouteUrl(isOnRecentRoute);
	const shouldHide = useGlobalItemVisibility(
		MENU_ID,
		isSelected || isFlyoutOpen,
		isHidden,
		peekingId,
		setPeekingId,
	);

	return (
		<KeyboardShortcutWrapper
			deprecatedKeyboardShortcut={!isEditorOpen ? GO_TO_RECENT_SHORTCUT : undefined}
			keyboardShortcut={UNIVERSAL_GO_TO_RECENT_SHORTCUT}
			isAppNavigationShortcut
			onShortcutTriggered={onShortcutTriggered}
		>
			{!shouldHide && (
				<div onMouseEnter={onHover} onFocus={onHover}>
					<FlyoutMenuItem onOpenChange={onOpenChange} isOpen={isFlyoutOpen}>
						<FlyoutMenuItemTrigger
							elemBefore={ClockIconComponent}
							onClick={onClick}
							isSelected={isSelected}
						>
							<FormattedMessage {...i18n.recent} />
						</FlyoutMenuItemTrigger>
						<FlyoutMenuItemContent onClose={() => isFlyoutOpen && closeFlyout()} autoFocus={false}>
							<RecentContentMenuLoader />
						</FlyoutMenuItemContent>
					</FlyoutMenuItem>
				</div>
			)}
		</KeyboardShortcutWrapper>
	);
};

const isOnRecentRoute = {
	selector: (routeUrl: string | undefined) => routeUrl === HOME.toUrl({ view: 'recent' }),
};
